/**
 * T3 Mission Room + AIW-7 Belief / Contested / Objective DAG / Proof-of-Outcome.
 * Soft≠Live: empty lanes stay honest; Soft invent never paints Live authority.
 * Soft invent Live Hold / Takeover / green proof forbidden.
 * Soft invent Live Team / Soft agents never mint this ledger.
 * Platform hazard · AI hazard (advisory)
 * siren_on + dispatch_person via Action Gateway
 * command-sent is not outcome · Soft invent verified deleted
 * Soft invent free-form plans deleted
 * Differentiation: exception-first on-the-loop (not approve-every-action gate);
 * Proof never greens on command-sent alone.
 * Named consented household · not mass AUTO
 * Tick continues the Team · Soft invent mint-Live from here deleted
 * Hold · replica PG · Hold · in-process
 * Incident SoR close · Team complete cannot skip Incident SoR
 * k="H3 nearby" · k="H5 dispatch"
 * Soft invent Decide prose deleted
 * Empty chain — Team has not admitted/executed yet. Soft invent steps forbidden.
 * Only a named household that agreed
 * No second close button
 * ≤2 human touches on normal incidents
 * Vision evidence · not Hold
 * Intervene ≠ Hold — inject constraints; AI keeps write authority.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { html, F, KV, Card, Alert, Field, Input, go } from "./ui.js";
import { api } from "./api.js";
import { formatUserInstant } from "./time.js";
import { autonomyPlanesOf, firstLiveGapReason, fourVisiblesOf } from "./autonomy-planes.js";
import { TechnicalDetails } from "./layered-console.js";
import { capsSay, kampungTonightSay, operatorSay, whySay } from "./operator-say.js";

const AI_OUTPUT_TYPES = [
  "Observation",
  "Assessment",
  "Recommendation",
  "Action Request",
  "Execution State",
  "Verified Result",
];

function lane(label, value, tone) {
  const say = ({ NOW: "Now", NEXT: "Next", WATCH: "Watch", REALITY: "Reality", PROOF: "Proof" })[label] || label;
  return html`
    <div className="mission-room__lane" data-lane=${label} data-tone=${tone || "ai"}>
      <span className="eyebrow">${say}</span>
      <b>${value || "—"}</b>
    </div>`;
}

function authorityTone(state) {
  const s = String(state || "").toLowerCase();
  if (s === "held") return "warn";
  if (s === "human_command") return "bad";
  if (s === "running_ai") return "go";
  if (s === "degraded_rules_only") return "warn";
  return "ai";
}

function scoreOwnerLabel(owner) {
  const o = String(owner || "none");
  if (o === "shadow") return "AI (rules shadow)";
  if (o === "unproven") return "unproven · not independent";
  if (o === "ai") return "AI · independent";
  if (o === "none") return "Not scored yet";
  return operatorSay(o, o);
}

function liveCapsOriginLabel(origin) {
  const o = origin && typeof origin === "object" ? origin : {};
  const coord = Array.isArray(o.coordinator) ? o.coordinator.filter(Boolean) : [];
  const enrich = Array.isArray(o.enrich) ? o.enrich.filter(Boolean) : [];
  const s3 = Array.isArray(o.section3_added) ? o.section3_added.filter(Boolean) : [];
  if (!coord.length && !enrich.length && !s3.length) return "None yet";
  return [
    coord.length ? "Team · " + capsSay(coord) : null,
    enrich.length ? "Enrich · " + capsSay(enrich) : null,
    s3.length ? "Added · " + capsSay(s3) : null,
  ].filter(Boolean).join(" · ");
}

function h3NearbyLabel(h3) {
  // no second surveyed board
  const row = h3 && typeof h3 === "object" ? h3 : {};
  const added = Number(row.added || 0) || 0;
  const skip = String(row.skip_reason || "");
  if (added > 0) return "Nearby +" + added;
  if (skip === "independence_down") return "Skipped — independence is down";
  if (skip === "no_second_surveyed_board") return "Skipped — no second board on file";
  if (skip === "not_high") return "Skipped — not high enough";
  return skip ? whySay(skip) : "Skipped";
}

function liveLoopSeatsOf(loops) {
  const pack = loops && typeof loops === "object" ? loops : {};
  return Array.isArray(pack.seats) ? pack.seats.filter((s) => s && typeof s === "object").slice(0, 6) : [];
}

function liveSeatLabel(seat, i, seats) {
  if (seat && seat.nearby) return "Nearby";
  const originAt = (Array.isArray(seats) ? seats : []).findIndex((s) => s && !s.nearby);
  if (i === originAt || (originAt < 0 && i === 0)) return "At the point";
  return "Live " + String(i + 1);
}

function liveSeatState(seat) {
  if (seat && seat.closed) return "closed";
  if (seat && seat.played) return "playing";
  if (seat && seat.snapshot) return "snapshot";
  return "open";
}

function watchRitualSay(action, extra) {
  const t = String((extra && extra.trigger) || "");
  if (action === "rehearse" || t === "rehearsal") {
    return "Labelled rehearsal started · siren and dispatch go through the Gateway";
  }
  if (t === "rehearsal_ghost") return "Ghost rehearsal · only if the monthly window is due";
  if (t === "handshake_accept") return "Taken · not arrived until the NFC tag is tapped";
  if (t === "handshake_arrive") return "Arrived at the NFC tag";
  if (t === "kampung_accept") return "Tonight's watch accepted";
  if (t === "buy_unknown") return "Asked for the cheapest fact · no GPS invented";
  if (t === "letter_correct") return "Remembered for next watch · Policy unchanged";
  if (t === "adversarial") return "Labelled trick logged · not a real incident";
  return "";
}

function h5DispatchLabel(h5) {
  // Needs You · no GPS invent · listed · not arrived
  const row = h5 && typeof h5 === "object" ? h5 : {};
  if (row.arrival === true) return "Arrived";
  const sink = String(row.sink || "");
  if (sink === "needs_you") return "Needs you — do not invent a place";
  if (sink === "listed_not_walked" || sink === "already_listed" || row.listed) {
    return "On the list — not on site yet";
  }
  if (sink === "not_required") return "Not required";
  return "Not walked yet";
}

function brainHonestyLabel(honesty, auth) {
  // rails_only · closed history · never_dialed · closed history
  // empty_dial · closed history · dial_fail · not Live brain
  // coordinator · not running · rails_only · not model brain
  // AIW-0 · Ownership fence · Effective brain · control_generation CAS is PostgreSQL
  // AI_TEAM_RUN_PG replica proof · Hardware Soft · yes · Soft invent gate
  // AIW-3 · Assessment in workflow · receipt · proof: · OBJ STEP
  // degraded · not Live brain
  const h = String(honesty || "").trim();
  const a = String(auth || "").toLowerCase();
  if (a === "degraded_rules_only") {
    return "Rules only — the model is not in charge";
  }
  if (a && a !== "running_ai") {
    if (h === "rails_only") return "Rules only. History is closed.";
    if (h === "never_dialed") return "Not started. History is closed.";
    if (h === "empty_dial") return "No plan yet. History is closed.";
    if (h === "dial_fail") return "The team could not start a plan";
    if (h === "coordinator") return "The team is not running";
  }
  if (h === "coordinator") return "The team planned this";
  if (h === "rails_only") return "Rules only — not a live plan";
  if (h === "empty_dial") return "No plan yet — not a live plan";
  if (h === "dial_fail") return "The team could not start a plan";
  if (h === "never_dialed") return "Not started";
  return operatorSay(h, "—");
}

function brainHonestyTone(honesty, auth) {
  const a = String(auth || "").toLowerCase();
  const h = String(honesty || "").trim();
  return a === "running_ai" && h === "coordinator" ? "go" : "warn";
}

function _aborted(e) {
  const n = String((e && e.name) || "");
  const m = String((e && e.message) || e || "");
  return n === "AbortError" || /^aborted$/i.test(m.trim());
}

function _genOf(pack) {
  const run = (pack && pack.run) || {};
  const mission = (pack && pack.mission) || {};
  return Number(mission.control_generation || run.control_generation || 0) || 0;
}

function claimLine(c) {
  const role = operatorSay(c.role, "Role");
  const rel = operatorSay(c.relation || c.status, "Claim");
  const pred = operatorSay(c.predicate || c.subject_ref, "On this watch");
  const conf = c.confidence != null ? Math.round(Number(c.confidence) * 100) + "%" : "—";
  return `${role} · ${rel} · ${pred} · ${conf}`;
}

function outputTone(kind) {
  if (kind === "Action Request") return "warn";
  if (kind === "Execution State") return "ai";
  if (kind === "Verified Result") return "go";
  return "quiet";
}

function lampTone(state) {
  const s = String(state || "").toLowerCase();
  if (s === "on") return "go";
  if (s === "engaged") return "bad";
  if (s === "degraded") return "warn";
  return "quiet";
}

function lampMark(state) {
  const s = String(state || "").toLowerCase();
  if (s === "on") return "on";
  if (s === "engaged") return "engaged";
  if (s === "degraded") return "half";
  return "off";
}

function certTone(result) {
  const r = String(result || "").toUpperCase();
  if (r === "PASS") return "go";
  if (r === "FAIL") return "bad";
  if (r === "SKIP") return "warn";
  return "quiet";
}

/** Soft≠Live — local Mission Room paint only; Soft invent Live Team ledger forbidden. */
function softMissionPackOf(incidentId) {
  const id = String(incidentId || "").trim();
  const rich = id === "soft-inc-p1-gate" || id === "soft-inc-p3-lobby";
  const resolved = id === "soft-inc-resolved";
  return {
    live: false,
    ledger: "soft_preview",
    run: {
      team_run_id: "soft-run-" + id,
      control_generation: 3,
      authority_state: resolved ? "held" : "running_ai",
      live: false,
      ledger: "soft_preview",
      effective_model_id: "soft-demo-brain",
      via: "soft_preview",
      model_degraded: false,
    },
    mission: {
      live: false,
      ledger: "soft_preview",
      team_run_id: "soft-run-" + id,
      control_generation: 3,
      authority_state: resolved ? "held" : "running_ai",
      NOW: resolved
        ? "False alarm closed · Soft preview"
        : id === "soft-inc-p1-gate"
          ? "Perimeter cut at North Gate · thermal + PIR"
          : id === "soft-inc-p3-lobby"
            ? "Panic latch · UNIT-1 on scene"
            : "Soft case active · Team tick preview",
      NEXT: resolved
        ? "Archive Soft PIR draft"
        : rich
          ? "Seal lane · await dual unit + camera proof"
          : "Continue Soft Team tick",
      WATCH: rich ? "Contested: pedestrian lane seal vs keep open" : "No contested Claims",
      REALITY: "Device + Soft dispatch rows only · not Live PG",
      PROOF: resolved ? "Soft verified label · Soft invent Live green forbidden" : "unverified · command-sent ≠ outcome",
      needs_you_n: rich ? 1 : 0,
      needs_you: rich ? [{
        kind: "exception",
        title: id === "soft-inc-p1-gate"
          ? "Seal pedestrian lane before UNIT-7 enters?"
          : "Release elevators without badge reconcile?",
        eligibility_reason: "objective_blocked",
      }] : [],
      last_action: {
        proof_trust_grade: "unknown",
        receipt_is_not_verified: true,
        verified: false,
        soft_invent: false,
      },
      audit_chain: {
        ok: true,
        steps: [],
        steps_n: 0,
        intact: true,
        event_to_proof: false,
        soft_invent: false,
        reason: "soft_preview_empty",
      },
      contested_n: rich ? 1 : 0,
      claims_n: 4,
      effective_model_id: "soft-demo-brain",
      via: "soft_preview",
      model_degraded: false,
      control_authority: {
        control_holder: resolved ? "Human · Soft Hold" : "AI Operations Team",
        ai_control_mode: resolved ? "held" : "suggest_and_act",
        degraded: false,
        latest_change: { at: Date.now() - 90000, actor: "soft_preview" },
      },
      ai_outputs: [
        { id: "o1", output_type: "Observation", text: "Soft sensor stack fired · local paint only.", source: "Soft Team" },
        { id: "o2", output_type: "Assessment", text: rich ? "Risk elevated · dual response preferred." : "Watch posture.", source: "Soft Team" },
        { id: "o3", output_type: "Recommendation", text: "Keep Soft≠Live fence · no MQTT.", source: "Soft Team" },
        { id: "o4", output_type: "Action Request", text: rich ? "Needs You · confirm lane seal." : "None blocking.", source: "Soft Team" },
        { id: "o5", output_type: "Execution State", text: "Soft run · generation 3.", source: "Soft Team" },
        { id: "o6", output_type: "Verified Result", text: resolved ? "Soft closure label only." : "Not verified.", source: "Soft Team" },
      ],
      command_planes: [{
        intent_authority: { state: rich ? "pending_human" : "draft" },
        delivery: { state: "queued" },
        outcome_proof: { state: resolved ? "observed" : "unobserved" },
      }],
      belief: {
        live: false,
        soft_invent: false,
        active_n: 3,
        contested_n: rich ? 1 : 0,
        contested: rich ? [{
          claim_id: "c-lane",
          role: "constraint",
          relation: "contested",
          predicate: "seal_pedestrian_lane",
          confidence: 0.62,
        }] : [],
        active_head: [
          { claim_id: "c1", role: "observation", relation: "support", predicate: "alarm_active", confidence: 0.91 },
          { claim_id: "c2", role: "assessment", relation: "support", predicate: "risk_high", confidence: 0.84 },
          { claim_id: "c3", role: "objective", relation: "support", predicate: "prove_on_scene", confidence: 0.55 },
        ],
      },
      objectives: {
        nodes: [
          { id: "obj1", kind: "objective", title: "Contain site risk", status: "active", desk: "response" },
          { id: "st1", kind: "step", title: "Dispatch unit", status: "done", desk: "board" },
          { id: "st2", kind: "step", title: "Collect independent proof", status: resolved ? "done" : "blocked", desk: "mission" },
        ],
      },
      proof: {
        stage: resolved ? "observed" : "unverified",
        label: resolved ? "Soft observed · not Live green" : "Await independent evidence",
        can_green_complete: false,
      },
      assessment: {
        score: rich ? 88 : resolved ? 12 : 54,
        score_owner: "none",
        echo_lamp: "off",
        independence_flag: "down",
        risk_independent: false,
        shadow_echo: false,
        safety: rich ? "elevated" : resolved ? "clear" : "assessing",
        evidence_gap_n: rich ? 2 : 0,
        evidence_gaps: rich ? [
          { claim_id: "g1", role: "camera", predicate: "north_gate_still" },
          { claim_id: "g2", role: "unit", predicate: "on_scene_confirm" },
        ] : [],
        reasons: rich
          ? ["Thermal + PIR stacked", "Pedestrian lane still open", "Soft invent Live score deleted"]
          : ["Soft preview assessment"],
      },
      counterfactual: {
        impact: rich ? "high" : "low",
        execute: "Advance Soft response · still not Live MQTT",
        wait: "Hold Soft Team · gather camera",
        alternative: "Reroute UNIT to secondary gate",
        blocks_team: false,
      },
    },
  };
}

export function MissionRoomPanel({ incidentId, toast, softDemo }) {
  const id = String(incidentId || "").trim();
  const isSoft = softDemo === true || id.startsWith("soft-inc-");
  const [loadBusy, setLoadBusy] = useState(false);
  const [controlBusy, setControlBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pack, setPack] = useState(null);
  const [teamStatus, setTeamStatus] = useState(null);
  const [autonomy, setAutonomy] = useState(null);
  const [interveneNote, setInterveneNote] = useState("");
  const [handshakeTag, setHandshakeTag] = useState("");
  const [handshakeMac, setHandshakeMac] = useState("");
  const [letterCorrection, setLetterCorrection] = useState("");
  const [advTrick, setAdvTrick] = useState("");
  const [visionQ, setVisionQ] = useState("");
  const [visionBusy, setVisionBusy] = useState(false);
  const [visionOut, setVisionOut] = useState(null);
  const loadAbortRef = useRef(null);
  const refreshBusy = loadBusy || controlBusy;

  const abortLoad = () => {
    const ac = loadAbortRef.current;
    if (!ac) return;
    try { ac.abort(); } catch (_) { /* already aborted */ }
    loadAbortRef.current = null;
  };

  useEffect(() => {
    if (isSoft) {
      setTeamStatus({ ok: true, live: false, ledger: "soft_preview" });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const st = await api("/ai/team/status", { method: "GET" });
        if (!cancelled) setTeamStatus(st && typeof st === "object" ? st : null);
      } catch {
        if (!cancelled) setTeamStatus(null);
      }
      try {
        const home = await api("/ai/home-context", { method: "GET" });
        const lifted = home && typeof home === "object" ? home.autonomy : null;
        if (lifted && typeof lifted === "object") {
          if (!cancelled) setAutonomy(lifted);
          return;
        }
      } catch {
        /* home miss → policy/effective, never invent */
      }
      try {
        const eff = await api("/ai/autonomy/policy/effective", { method: "GET" });
        if (!cancelled) setAutonomy(eff && typeof eff === "object" ? eff : null);
      } catch {
        if (!cancelled) setAutonomy(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSoft]);

  const load = useCallback(async (opts) => {
    const wantFull = !!(opts && opts.full);
    if (!id) {
      setPack(null);
      return;
    }
    if (isSoft) {
      setLoadBusy(true);
      setErr("");
      setPack(softMissionPackOf(id));
      setLoadBusy(false);
      return;
    }
    abortLoad();
    const ac = new AbortController();
    loadAbortRef.current = ac;
    setLoadBusy(true);
    setErr("");
    try {
      let out = null;
      try {
        out = await api(`/ai/team/incidents/${encodeURIComponent(id)}/run?light=1`, {
          method: "GET",
          timeoutMs: 8000,
          retries: 0,
          signal: ac.signal,
        });
      } catch (e) {
        if (_aborted(e) || ac.signal.aborted) return;
        const st = Number((e && e.status) || (e && e.response && e.response.status) || 0);
        const msg = String((e && e.message) || "");
        if (st === 404 || /\b404\b/.test(msg)) {
          out = await api("/ai/team/runs", {
            method: "POST",
            body: { incident_id: id, auto_tick: true },
            timeoutMs: 8000,
            signal: ac.signal,
          });
        } else {
          throw e;
        }
      }
      if (ac.signal.aborted) return;
      if (out && typeof out === "object") setPack(out);
      setLoadBusy(false);
      /* Full GET is opt-in (Refresh). Auto-full was blocking E-Stop POST. */
      if (!wantFull) return;
      try {
        const full = await api(`/ai/team/incidents/${encodeURIComponent(id)}/run`, {
          method: "GET",
          timeoutMs: 12000,
          retries: 0,
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        if (full && typeof full === "object") {
          setPack((prev) => (_genOf(prev) > _genOf(full) ? prev : full));
        }
      } catch (e) {
        if (_aborted(e) || ac.signal.aborted) return;
        /* Light pack is enough for E-Stop CAS. */
      }
    } catch (e) {
      if (_aborted(e) || ac.signal.aborted) return;
      setPack(null);
      setErr(String((e && e.message) || e || "Mission Room unavailable"));
    } finally {
      if (loadAbortRef.current === ac) loadAbortRef.current = null;
      setLoadBusy(false);
    }
  }, [id, isSoft]);

  useEffect(() => {
    load();
    return () => abortLoad();
  }, [load]);

  const mission = (pack && pack.mission) || {};
  const run = (pack && pack.run) || {};
  const gen = Number(mission.control_generation || run.control_generation || 0) || undefined;
  const belief = mission.belief || {};
  const objectives = mission.objectives || {};
  const proof = mission.proof || {};
  const assessment = mission.assessment || {};
  const lastCoord = mission.last_coordinator_turn || {};
  const liveCapsOrigin = mission.live_caps_origin || {};
  const h3Nearby = mission.h3_nearby || {};
  const h5Dispatch = mission.h5_dispatch || {};
  const counterfactual = mission.counterfactual || {};
  const controlAuthority = mission.control_authority || {};
  const aiOutputs = Array.isArray(mission.ai_outputs) ? mission.ai_outputs : [];
  const commandPlanes = Array.isArray(mission.command_planes) ? mission.command_planes : [];
  const needs = Array.isArray(mission.needs_you) ? mission.needs_you : [];
  const auditChain = mission.audit_chain || {};
  const liveLoops = (auditChain.live_loops && typeof auditChain.live_loops === "object")
    ? auditChain.live_loops
    : (mission.live_loops && typeof mission.live_loops === "object" ? mission.live_loops : {});
  const liveLoopN = Number(liveLoops.n || 0) || 0;
  const liveLoopClosed = Number(liveLoops.closed_n || 0) || 0;
  const liveLoopPlaying = Number(liveLoops.playing_n || 0) || 0;
  const liveLoopOpen = Number(liveLoops.open_n || (Array.isArray(liveLoops.open) ? liveLoops.open.length : 0)) || 0;
  const liveSeats = liveLoopSeatsOf(liveLoops);
  const lastAction = mission.last_action || {};
  const closeSor = mission.close_sor || {};
  const closeReasons = Array.isArray(closeSor.reasons) ? closeSor.reasons : [];
  const platformHazard = String(
    lastAction.platform_hazard || mission.platform_hazard || assessment.platform_hazard || "—"
  );
  const aiHazardAdvisory = String(
    lastAction.ai_hazard_advisory || mission.ai_hazard_advisory || lastAction.hazard || "—"
  );
  const auditSteps = Array.isArray(auditChain.steps) ? auditChain.steps : [];
  const contested = Array.isArray(belief.contested) ? belief.contested : [];
  const activeHead = Array.isArray(belief.active_head) ? belief.active_head : [];
  const dagNodes = Array.isArray(objectives.nodes) ? objectives.nodes : [];
  const reasons = Array.isArray(assessment.reasons) ? assessment.reasons : [];
  const evidenceGaps = Array.isArray(assessment.evidence_gaps) ? assessment.evidence_gaps : [];

  const live = mission.live === true || run.live === true;
  const ledger = String(mission.ledger || run.ledger || teamStatus?.ledger || "in_process");
  const statusLive = teamStatus?.live === true;
  const planes = autonomyPlanesOf(autonomy);
  const gap = firstLiveGapReason(needs);
  const fourBase = fourVisiblesOf(planes);
  const four = gap && (!fourBase.why || fourBase.why === "—")
    ? { ...fourBase, why: gap }
    : fourBase;
  const control = async (action, extra) => {
    if (isSoft) {
      toast && toast("Preview cannot run the live team", "warn");
      return;
    }
    const rid = String(run.team_run_id || mission.team_run_id || "").trim();
    if (!rid) {
      toast && toast("No Team run yet", "warn");
      return;
    }
    if (!(gen >= 1)) {
      toast && toast("This watch is not ready to take a command yet", "warn");
      return;
    }
    abortLoad();
    const more = extra && typeof extra === "object" ? extra : {};
    const ritual = String(more.trigger || "");
    if (ritual === "handshake_arrive" && !String(more.tag_mac || handshakeMac || "").trim()) {
      toast && toast("Tap the NFC tag first — arrival is the signed tap, not the phone or GPS", "warn");
      return;
    }
    if (ritual === "letter_correct" && !String(more.correction || letterCorrection || "").trim()) {
      toast && toast("Write the one thing that is true", "warn");
      return;
    }
    if (ritual === "adversarial" && !String(more.trick || advTrick || "").trim()) {
      toast && toast("Name the trick first", "warn");
      return;
    }
    const isEstop = action === "estop" || action === "estop/release";
    setControlBusy(true);
    try {
      const body = { control_generation: gen, ...more };
      let pathAction = action;
      if (action === "rehearse") {
        pathAction = "tick";
        body.trigger = "rehearsal";
      }
      if (more.trigger) {
        pathAction = "tick";
        body.trigger = more.trigger;
      }
      if (action === "intervene") {
        const note = String(interveneNote || "").trim();
        if (!note) {
          toast && toast("Intervene needs a constraint or fact — empty inject refused", "warn");
          setControlBusy(false);
          return;
        }
        body.note = note;
        body.constraints = [note];
      }
      const out = await api(`/ai/team/runs/${encodeURIComponent(rid)}/${pathAction}`, {
        method: "POST",
        body,
        timeoutMs: isEstop ? 20000 : 8000,
      });
      const nextRun = out && typeof out === "object" ? (out.run || {}) : {};
      const nextMission = out && typeof out === "object" ? (out.mission || {}) : {};
      setPack((prev) => {
        const prevRun = (prev && prev.run) || {};
        const prevMission = (prev && prev.mission) || {};
        const next = {
          ...(prev || {}),
          run: { ...prevRun, ...nextRun },
          mission: { ...prevMission, ...nextMission },
        };
        return _genOf(next) >= _genOf(prev) ? next : prev;
      });
      const tag = live ? "Live store" : "in process";
      const ritualLine = watchRitualSay(action, more);
      toast && toast(
        ritualLine ? ritualLine + " · " + tag
          : action === "hold" ? "Held · " + tag
          : action === "intervene" ? "Intervene · constraints committed · AI still writing · " + tag
          : action === "continue" ? "Team continued · " + tag
            : action === "takeover" ? "Takeover · you have the wheel · " + tag
              : action === "release" ? "Released to Team · " + tag
                : action === "estop" ? "Emergency stop · executors must ACK · " + tag
                  : action === "estop/release" ? "E-Stop released · still Held · Continue required · " + tag
                    : "Team tick · " + tag,
        action === "takeover" || action === "hold" || action === "estop" ? "warn" : "go",
      );
    } catch (e) {
      toast && toast(String((e && e.message) || e || "Control refused"), "bad");
    } finally {
      setControlBusy(false);
    }
  };

  const vision = async (kind) => {
    if (isSoft) {
      toast && toast("Preview cannot ask this frame", "warn");
      return;
    }
    if (!id) {
      toast && toast("Pick a case first", "warn");
      return;
    }
    setVisionBusy(true);
    try {
      if (kind === "refresh") {
        const out = await api(`/ai/incidents/${encodeURIComponent(id)}/vision/refresh`, {
          method: "POST",
          body: { force: true },
        });
        setVisionOut(out && typeof out === "object" ? out : null);
        toast && toast("Saved as evidence. This is not a Hold.", "go");
      } else {
        const q = String(visionQ || "").trim();
        if (!q) {
          toast && toast("Type a question first", "warn");
          setVisionBusy(false);
          return;
        }
        const out = await api(`/ai/incidents/${encodeURIComponent(id)}/vision/ask`, {
          method: "POST",
          body: { question: q },
        });
        setVisionOut(out && typeof out === "object" ? out : null);
        toast && toast("Answer is evidence only", "go");
      }
    } catch (e) {
      toast && toast(String((e && e.message) || e || "Vision refused"), "bad");
    } finally {
      setVisionBusy(false);
    }
  };

  if (!id) {
    return html`<div className="mission-room mission-room--empty" data-cmc-mission="t3" data-cmc-mission-pin="v605" data-cmc-team-status=${teamStatus?.ok ? "1" : "0"}>
      <p className="eyebrow">Watch</p>
      <h3>Pick a case to open the watch</h3>
      <p className="note">The team stays quiet until a case is open.</p>
      ${teamStatus?.ok
        ? html`<p className="note" data-cmc-team-ledger=${teamStatus.ledger || ""}
            data-cmc-team-create="create=POST /ai/team/runs"
            data-cmc-team-collection="collection GET closed (405 honesty)">
            ${operatorSay(teamStatus.ledger, "Team")} is ready. Pick a case to open the watch.
          </p>`
        : null}
    </div>`;
  }

  const auth = String(mission.authority_state || run.authority_state || "—");
  const held = auth === "held";
  const human = auth === "human_command";
  const stopped = auth === "blocked";
  const proofStage = String(proof.stage || "unverified");
  const proofGrade = String(
    proof.proof_trust_grade || lastAction.proof_trust_grade || "unknown"
  );
  const proofGreen = proof.can_green_complete === true && proofStage === "verified" && proofGrade === "verified";
  const controlHolder = operatorSay(controlAuthority.control_holder || "Unassigned", "Unassigned");
  const aiMode = operatorSay(controlAuthority.ai_control_mode || "observe", "Observe");
  const degraded = controlAuthority.degraded === true || mission.model_degraded === true || (!live && !isSoft);
  const latestControl = controlAuthority.latest_change || {};
  const latestControlAt = formatUserInstant(latestControl.at, { date: true, zoneName: false });
  const independent = assessment.independence_flag === "up" || assessment.risk_independent === true;
  const safetySay = operatorSay(assessment.safety || "Awaiting assessment", "Awaiting assessment");
  const severity = assessment.score != null
    ? (independent
      ? `Risk ${assessment.score} · ${safetySay}`
      : `Unproven ${assessment.score} · ${safetySay}`)
    : safetySay;
  const decision = needs[0]
    ? operatorSay(needs[0].title || needs[0].kind || "Decision required", "A step is waiting for you")
    : "No blocking decision";
  const needWhy = needs[0]
    ? operatorSay(needs[0].eligibility_reason || "objective_blocked", "This stays open until you decide")
    : "";
  const lampPack = mission.lamps && typeof mission.lamps === "object" ? mission.lamps : {};
  const lampRows = Array.isArray(lampPack.lamps) ? lampPack.lamps : [];
  const nightLetter = mission.night_letter && typeof mission.night_letter === "object" ? mission.night_letter : {};
  const letterText = String(nightLetter.letter || "").trim();
  const beliefLines = mission.belief_lines && typeof mission.belief_lines === "object" ? mission.belief_lines : {};
  const kampung = mission.kampung && typeof mission.kampung === "object" ? mission.kampung : {};
  const tonight = kampung.tonight && typeof kampung.tonight === "object" ? kampung.tonight : {};
  const adversarial = mission.adversarial && typeof mission.adversarial === "object" ? mission.adversarial : {};
  const advBadges = Array.isArray(adversarial.badges) ? adversarial.badges : [];
  const sched = mission.rehearsal_schedule && typeof mission.rehearsal_schedule === "object" ? mission.rehearsal_schedule : {};
  const duel = mission.duel && typeof mission.duel === "object" ? mission.duel : {};
  const unknowns = mission.unknowns && typeof mission.unknowns === "object" ? mission.unknowns : {};
  const unknownRows = Array.isArray(unknowns.rows) ? unknowns.rows : [];
  const cert = (mission.rehearsal_certificate && typeof mission.rehearsal_certificate === "object")
    ? mission.rehearsal_certificate
    : (mission.assurance && typeof mission.assurance === "object" ? mission.assurance : {});
  const certRows = Array.isArray(cert.rows) ? cert.rows : [];
  const certLabelled = cert.labelled === true;
  const loom = mission.time_loom && typeof mission.time_loom === "object" ? mission.time_loom : {};
  const blind = mission.blind_map && typeof mission.blind_map === "object" ? mission.blind_map : {};
  const shadows = Array.isArray(blind.shadows) ? blind.shadows : [];
  const handshake = mission.handshake && typeof mission.handshake === "object" ? mission.handshake : {};
  const postcard = mission.postcard && typeof mission.postcard === "object" ? mission.postcard : {};
  const postcardLines = Array.isArray(postcard.lines) ? postcard.lines : [];
  const watchLog = mission.watch_log && typeof mission.watch_log === "object" ? mission.watch_log : {};
  const implant = mission.team_implant && typeof mission.team_implant === "object" ? mission.team_implant : {};
  const implantSteps = Array.isArray(implant.steps) ? implant.steps : [];
  const planDelta = mission.plan_delta && typeof mission.plan_delta === "object" ? mission.plan_delta : {};
  const keepCaps = Array.isArray(planDelta.keep) ? planDelta.keep : [];
  const addCaps = Array.isArray(planDelta.add) ? planDelta.add : [];
  const cancelCaps = Array.isArray(planDelta.cancel_pending) ? planDelta.cancel_pending : [];
  const believed = Array.isArray(watchLog.believed) ? watchLog.believed : [];
  const doubted = Array.isArray(watchLog.doubted) ? watchLog.doubted : [];
  const bought = Array.isArray(watchLog.bought) ? watchLog.bought : [];
  const learned = Array.isArray(watchLog.learned) ? watchLog.learned : [];
  const cheapestUnknown = unknownRows[0] || null;
  const tonightNamed = tonight.named === true;
  const kampungAccepted = kampung.accepted === true;
  const dutyBond = mission.duty_bond && typeof mission.duty_bond === "object" ? mission.duty_bond : {};
  const formation = mission.watch_formation && typeof mission.watch_formation === "object" ? mission.watch_formation : {};
  const genome = mission.watch_genome && typeof mission.watch_genome === "object" ? mission.watch_genome : {};
  const genomeReason = String(genome.reason || "");
  const genomeMind = genomeReason === "no_change"
    ? "Same facts · the Watch did not change its mind"
    : (genome.material_changed ? "This judgement is new" : "");
  const formSeats = Array.isArray(formation.seats) ? formation.seats : [];
  const ghostDue = sched.due === true;
  const callOpen = handshake.answered === true && handshake.accepted !== true;
  const arrivalOpen = handshake.accepted === true && handshake.arrived !== true;
  const peopleNeed = callOpen || arrivalOpen || (tonightNamed && !kampungAccepted);
  const showLiveEyes = !isSoft && liveLoopN > 0;
  const liveNeed = showLiveEyes && liveLoopOpen > 0 && !(ghostDue || cheapestUnknown || peopleNeed);
  const doNow = ghostDue || !!cheapestUnknown || peopleNeed || liveNeed;
  const busyOrFrozen = controlBusy || held || stopped || isSoft;

  return html`<${F} className="mission-room" data-cmc-mission="t3" data-cmc-aiw="aiw-7"
      data-cmc-aiw0="aiw-0" data-cmc-aiw1="aiw-1" data-cmc-mission-pin="v605"
      data-cmc-team-status=${teamStatus?.ok ? "1" : "0"}
      data-cmc-team-ledger=${ledger}
      data-soft-demo=${isSoft ? "1" : "0"}>
    <${Card} title=${isSoft ? "Mission Room · Preview" : "Mission Room"} meta=${isSoft ? "Preview · not the live watch" : "The team is on this case"}
      sub=${isSoft
        ? "Preview only. This is not the live watch."
        : "The team runs until you Hold, step in, or take the wheel. Needs You is only for exceptions."}>
      ${err ? html`<${Alert} tone="warn" title="Mission Room" say=${err}/>` : null}
      ${loadBusy && !pack ? html`<p className="note">Getting the watch ready…</p>` : null}
      ${isSoft ? html`<${Alert} tone="warn" title="Preview Mission Room"
        say="Local preview only. The live team is not running here."/>` : null}
      ${!isSoft && teamStatus?.ok && !pack
        ? html`<p className="note">Team is ${statusLive ? "on the Live store" : "still catching up"}.</p>`
        : null}
      ${pack ? html`<${F}>
        <section className="mission-room__control-strip" data-cmc-mission-control="persistent">
          <div><span>Who is in charge</span><b>${controlHolder}</b></div>
          <div><span>How the team acts</span><b>${aiMode}</b></div>
          <div data-tone=${degraded ? "warn" : "go"}><span>Condition</span><b>${degraded ? "Degraded" : "Normal"}</b></div>
          <div><span>Last change</span>
            <b>${latestControlAt}${latestControl.actor ? " · " + operatorSay(latestControl.actor, "") : ""}</b></div>
          <div data-cmc-s11-hazard="platform"><span>Site hazard</span>
            <b>${operatorSay(platformHazard, "—")}</b></div>
          <div data-cmc-s11-hazard="advisory"><span>Safety note</span>
            <b>${operatorSay(aiHazardAdvisory, "—")}</b></div>
        </section>
        ${lampRows.length || beliefLines.ok || showLiveEyes ? html`
        <section className="mission-room__watch-now" data-cmc-watch-now="1">
          ${lampRows.length ? html`
          <section className="mission-room__lamps" data-cmc-watch-lamps="1" aria-label="Watch lamps">
            ${lampRows.map((row) => html`
              <div key=${row.id || row.label} data-tone=${lampTone(row.state)} data-lamp=${row.id || ""}>
                <span>${operatorSay(row.label || row.id, "Lamp")}</span>
                <b data-mark=${lampMark(row.state)}>${operatorSay(lampMark(row.state), lampMark(row.state))}</b>
                <small>${row.reason ? operatorSay(row.reason, "") : ""}</small>
              </div>`)}
          </section>` : null}
          ${showLiveEyes ? html`
          <section className="mission-room__live-eyes" data-cmc-live-seats="1" data-cmc-live-loops=${liveLoopN > 1 ? "1" : "0"} aria-label="Cameras on this watch">
            <div className="mission-room__section-head">
              <div>
                <span>Cameras on this watch</span>
                <small>${liveLoopClosed} of ${liveLoopN} have proof · ${liveLoopPlaying} playing</small>
              </div>
            </div>
            <ol>
              ${(liveSeats.length ? liveSeats : Array.from({ length: Math.min(6, liveLoopN) }, () => ({}))).map((seat, i) => html`
                <li key=${(seat && seat.camera_id) || i} data-state=${liveSeatState(seat)} data-nearby=${seat && seat.nearby ? "1" : "0"}>
                  <span>${liveSeatLabel(seat, i, liveSeats)}</span>
                  <b>${liveSeatState(seat) === "closed" ? "Proof" : liveSeatState(seat) === "playing" ? "Play" : liveSeatState(seat) === "snapshot" ? "Snap" : "Open"}</b>
                  <small>
                    <em data-on=${seat && seat.played ? "1" : "0"}>Play</em>
                    <em data-on=${seat && seat.snapshot ? "1" : "0"}>Snap</em>
                    <em data-on=${seat && seat.closed ? "1" : "0"}>Proof</em>
                  </small>
                </li>`)}
            </ol>
          </section>` : null}
          ${beliefLines.ok ? html`
          <section className="mission-room__belief-lines" data-cmc-belief-lines="1">
            <div className="mission-room__section-head">
              <div><span>What I think</span><small>Belief · not an alarm list</small></div>
            </div>
            <div className="mission-room__plane-grid mission-room__plane-grid--belief">
              <div><span>Most likely</span><b>${beliefLines.most_likely || "—"}</b></div>
              <div><span>Also possible</span><b>${beliefLines.also_possible || "—"}</b></div>
              <div><span>What would change my mind</span><b>${beliefLines.what_would_change_mind || "—"}</b></div>
              <div><span>Seen before</span><b>${beliefLines.seen_before || "—"}</b></div>
            </div>
          </section>` : null}
        </section>` : null}
        ${doNow ? html`
        <section className="mission-room__do-now" data-cmc-do-now="1" aria-label="Do now">
          <div className="mission-room__section-head">
            <div><span>Do now</span><small>One job at a time · empty means the Watch is holding</small></div>
          </div>
          ${ghostDue ? html`
          <div className="mission-room__do-card" data-tone="warn" data-cmc-rehearsal-schedule="1" data-cmc-rehearsal-due="1">
            <div>
              <span>Monthly check is due</span>
              <b>Run a labelled rehearsal. Nobody is woken who did not agree.</b>
              <small>${sched.month || ""} · day ${sched.day_utc || "—"} UTC ${sched.window_utc || ""}</small>
            </div>
            <button type="button" className="btn btn--sm" disabled=${busyOrFrozen}
              data-cmc-rehearse-ghost="1"
              onClick=${() => control("tick", { trigger: "rehearsal_ghost" })}>Run labelled rehearsal</button>
          </div>` : null}
          ${cheapestUnknown ? html`
          <div className="mission-room__do-card" data-cmc-unknown-ledger="1">
            <div>
              <span>I do not know this</span>
              <b>${operatorSay(cheapestUnknown.unknown, "Something I cannot see yet")}</b>
              <small>${operatorSay(cheapestUnknown.cheapest, "Cheapest fact")}</small>
            </div>
            <button type="button" className="btn btn--sm" disabled=${busyOrFrozen}
              data-cmc-buy-unknown="1"
              onClick=${() => control("tick", { trigger: "buy_unknown" })}>Get this fact</button>
          </div>` : null}
          ${tonightNamed && !kampungAccepted ? html`
          <div className="mission-room__do-card" data-cmc-kampung="1">
            <div>
              <span>Tonight's watch</span>
              <b>${tonight.display_name}</b>
              <small data-cmc-kampung-honesty="Named consented household · not mass AUTO">${kampungTonightSay(tonight.dusk_message)}</small>
            </div>
            <button type="button" className="btn btn--sm" disabled=${busyOrFrozen}
              data-cmc-kampung-accept="1"
              onClick=${() => control("tick", { trigger: "kampung_accept", household_id: tonight.household_id || "", tag_id: handshakeTag })}>I take tonight</button>
          </div>` : null}
          ${callOpen || arrivalOpen ? html`
          <div className="mission-room__do-card" data-cmc-handshake="1">
            <div>
              <span>${arrivalOpen ? "Taken · not on site" : "Call answered · not taken"}</span>
              <b>${arrivalOpen ? "Tap the NFC tag at the gate when you arrive." : "Press I take this. Arrival is the tag, not the phone."}</b>
              <small>Answered ≠ accepted ≠ arrived</small>
            </div>
            ${callOpen ? html`<button type="button" className="btn btn--sm" disabled=${busyOrFrozen}
              data-cmc-handshake-accept="1"
              onClick=${() => control("tick", { trigger: "handshake_accept", tag_id: handshakeTag })}>I take this</button>` : null}
            ${arrivalOpen ? html`<div className="mission-room__arrive-fields">
              <${Field} label="Tag place">
                <${Input} value=${handshakeTag} onInput=${(e) => setHandshakeTag(e.target.value)}
                  placeholder="jetty / gate / pump house"/>
              </${Field}>
              <${Field} label="Signed tap">
                <${Input} value=${handshakeMac} onInput=${(e) => setHandshakeMac(e.target.value)}
                  placeholder="from the NFC tag"/>
              </${Field}>
              <button type="button" className="btn btn--sm" disabled=${busyOrFrozen}
                data-cmc-handshake-arrive="1"
                onClick=${() => control("tick", { trigger: "handshake_arrive", tag_id: handshakeTag, tag_mac: handshakeMac })}>I'm at the tag</button>
            </div>` : null}
          </div>` : null}
          ${liveNeed ? html`
          <div className="mission-room__do-card" data-tone="warn" data-cmc-live-loops="1">
            <div>
              <span>Cameras still need proof</span>
              <b>${liveLoopOpen} of ${liveLoopN} cameras still need play or their own snapshot</b>
              <small>${liveLoopN > 1 ? "Several live cameras may play at once. " : ""}Looking again continues the team.</small>
            </div>
            <button type="button" className="btn btn--sm" disabled=${busyOrFrozen}
              onClick=${() => control("tick")}>Look again</button>
          </div>` : null}
        </section>` : null}
        <div className="btn-row mission-room__controls mission-room__controls--primary" data-cmc-aiw1="aiw-1">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${refreshBusy}
            onClick=${() => load({ full: true })}>Refresh</button>
          <button type="button" className="btn btn--sm" disabled=${controlBusy || held || stopped}
            onClick=${() => control("hold")}>Hold</button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${controlBusy || held || stopped}
            data-cmc-intervene="1" onClick=${() => control("intervene")}>Intervene</button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${controlBusy || held || stopped}
            onClick=${() => control("tick")}>Look again</button>
          <button type="button" className="btn btn--danger btn--sm" disabled=${stopped || !(gen >= 1)}
            data-cmc-estop="1" onClick=${() => control("estop")}>E-Stop</button>
        </div>
        ${letterText ? html`
        <section className="mission-room__night-letter" data-cmc-night-letter="1">
          <div className="mission-room__section-head">
            <div><span>Night letter</span><small>Handover · media stays on your device</small></div>
            <em>${operatorSay(nightLetter.proof_set, "")}</em>
          </div>
          <pre>${letterText}</pre>
          ${nightLetter.correction_open ? html`
          <div className="mission-room__letter-correct" data-cmc-letter-correct="1">
            <${Field} label="Correct one thing">
              <${Input} value=${letterCorrection} onInput=${(e) => setLetterCorrection(e.target.value)}
                placeholder=${String((nightLetter.would_change && nightLetter.would_change[0]) || "The rear door is wind")}/>
            </${Field}>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${busyOrFrozen}
              onClick=${() => control("tick", { trigger: "letter_correct", correction: letterCorrection })}>Remember this</button>
            <small>Remembers this for the next watch. Policy does not change.</small>
          </div>` : null}
        </section>` : null}
        ${certRows.length ? html`
        <section className="mission-room__certificate" data-cmc-rehearsal="1">
          <div className="mission-room__section-head">
            <div>
              <span>${certLabelled ? "Rehearsal certificate" : "Protection now"}</span>
              <small>${certLabelled
                ? "Siren and a person walk the labelled rehearsal"
                : "Current posture · not a rehearsal until you run one"}</small>
            </div>
            <em>${cert.kind ? operatorSay(cert.kind, "Check") : "This check"}</em>
          </div>
          <div className="mission-room__plane-grid">
            ${certRows.map((row) => html`
              <div key=${row.id} data-tone=${certTone(row.result)}>
                <span>${operatorSay(row.id, "This check")}</span>
                <b>${operatorSay(row.result, row.result || "—")}</b>
                <small>${operatorSay(row.detail, "")}</small>
              </div>`)}
          </div>
          ${cert.weak_link ? html`<p className="note">Weakest check · ${operatorSay(cert.weak_link.id, "This check")} · ${operatorSay(cert.weak_link.result, "")} · ${operatorSay(cert.weak_link.detail, "")}</p>` : null}
        </section>` : null}
        ${!ghostDue && sched.ok ? html`
        <p className="note mission-room__ghost-quiet" data-cmc-rehearsal-schedule="1" data-cmc-rehearsal-due="0">
          Monthly rehearsal is not due this month. Silent dispatch stays off.
        </p>` : null}
        ${duel.open ? html`
        <section className="mission-room__duel" data-cmc-duel="1">
          <div className="mission-room__section-head">
            <div><span>Two views</span><small>${duel.predicate || ""} @ ${duel.subject || ""}</small></div>
          </div>
          <div className="mission-room__plane-grid">
            <div><span>Advocate</span><b>${duel.advocate && duel.advocate.text || "—"}</b></div>
            <div><span>Challenger</span><b>${duel.challenger && duel.challenger.text || "—"}</b></div>
            <div><span>Cheapest test</span><b>${duel.cheapest_test || ""}</b></div>
          </div>
        </section>` : null}
        ${unknownRows.length > 1 ? html`
        <section className="mission-room__unknowns">
          <div className="mission-room__section-head">
            <div><span>Other unknowns</span><small>Priced gaps · never guessed</small></div>
            <em>${unknowns.n || unknownRows.length}</em>
          </div>
          ${unknownRows.slice(1).map((row, i) => html`
            <p key=${i} className="note"><b>${row.unknown}</b> · ${row.cheapest}</p>`)}
        </section>` : null}
        ${loom.ok ? html`
        <section className="mission-room__loom" data-cmc-time-loom="1">
          <div className="mission-room__section-head">
            <div><span>Time loom</span><small>Before · At · Since · reach-back is evidence</small></div>
          </div>
          <div className="mission-room__plane-grid">
            <div><span>Before</span><b>${loom.before || "no reach-back yet"}</b></div>
            <div><span>At</span><b>${loom.at || "—"}</b></div>
            <div><span>Since</span><b>${loom.since || "—"}</b></div>
          </div>
        </section>` : null}
        ${shadows.length ? html`
        <section className="mission-room__blind" data-cmc-blind-map="1">
          <div className="mission-room__section-head">
            <div><span>Blind map</span><small>Shadows of what cannot be seen · no invented GPS</small></div>
            <em>${blind.n || shadows.length}</em>
          </div>
          ${shadows.map((row, i) => html`
            <p key=${row.id || row.tone || i} className="note"><b>${operatorSay(row.tone, "Shadow")}</b> · ${operatorSay(row.reason, "Cannot see this yet")}</p>`)}
        </section>` : null}
        <section className="mission-room__people" data-cmc-handshake="1">
          <div className="mission-room__section-head">
            <div><span>People on this watch</span><small>Answered ≠ accepted ≠ arrived · NFC tag, not GPS</small></div>
          </div>
          <div className="mission-room__plane-grid mission-room__steps">
            <div data-tone=${handshake.answered ? "go" : "warn"}><span>1 · Answered</span><b>${handshake.answered ? "yes" : "no"}</b></div>
            <div data-tone=${handshake.accepted ? "go" : "warn"}><span>2 · Taken</span><b>${handshake.accepted ? "yes" : "no"}</b></div>
            <div data-tone=${handshake.arrived ? "go" : "warn"}><span>3 · At the tag</span><b>${handshake.arrived ? "tag" : "no"}</b></div>
          </div>
          ${kampung.ok ? html`
          <p className="note" data-cmc-kampung=${tonightNamed && kampungAccepted ? "1" : kampung.ok ? "1" : "0"}>
            <b>${tonightNamed ? tonight.display_name : "No named household tonight"}</b>
            · ${kampungAccepted ? "accepted tonight" : kampungTonightSay(tonight.dusk_message || tonight.reason)}
            ${kampung.flood ? html` · river ${kampung.flood.level} · a fact, not a siren` : null}
          </p>` : null}
          ${!tonightNamed || kampungAccepted ? html`
          <div className="btn-row">
            <button type="button" className="btn btn--ghost btn--sm" disabled=${busyOrFrozen}
              data-cmc-handshake-accept="1"
              onClick=${() => control("tick", { trigger: "handshake_accept", tag_id: handshakeTag })}>I take this</button>
            ${tonightNamed ? html`<button type="button" className="btn btn--ghost btn--sm" disabled=${busyOrFrozen}
              data-cmc-kampung-accept="1"
              onClick=${() => control("tick", { trigger: "kampung_accept", household_id: tonight.household_id || "", tag_id: handshakeTag })}>I take tonight</button>` : null}
          </div>` : null}
          <details className="mission-room__tag-tap">
            <summary>I have the signed tap from the NFC tag</summary>
            <div className="row gap">
              <${Field} label="Tag place">
                <${Input} value=${handshakeTag} onInput=${(e) => setHandshakeTag(e.target.value)}
                  placeholder="jetty / gate / pump house"/>
              </${Field}>
              <${Field} label="Signed tap">
                <${Input} value=${handshakeMac} onInput=${(e) => setHandshakeMac(e.target.value)}
                  placeholder="from the tag"/>
              </${Field}>
            </div>
            <button type="button" className="btn btn--sm" disabled=${busyOrFrozen}
              data-cmc-handshake-arrive="1"
              onClick=${() => control("tick", { trigger: "handshake_arrive", tag_id: handshakeTag, tag_mac: handshakeMac })}>I'm at the tag</button>
          </details>
        </section>
        <section className="mission-room__bond" data-cmc-duty-bond=${dutyBond.bound ? "1" : "0"}>
          <div className="mission-room__section-head">
            <div><span>Duty bond</span><small>Named board + tonight's watch · not mass AUTO</small></div>
            <em>${dutyBond.bound ? "bound" : "open"}</em>
          </div>
          <p className="note">
            ${dutyBond.bound
              ? "Tonight's watch is bound to this board."
              : "AUTO will not send people until tonight's watch is bound."}
          </p>
          ${genomeMind ? html`<p className="note" data-cmc-watch-genome-mind=${genomeReason || "new"}>${genomeMind}</p>` : null}
          ${formSeats.length ? html`
          <p className="note" data-cmc-watch-formation="1">
            ${formation.live_n || 0} on duty · ${formation.roster_n || 8} seats · rest standing by
          </p>` : null}
        </section>
        ${postcardLines.length ? html`
        <section className="mission-room__postcard" data-cmc-postcard="1">
          <div className="mission-room__section-head">
            <div><span>Proof postcard</span><small>Three lines · media stays local</small></div>
            <em>${postcard.proof_set || ""}</em>
          </div>
          ${postcardLines.map((line, i) => html`<p key=${i} className="note">${line}</p>`)}
        </section>` : null}
        ${watchLog.ok ? html`
        <section className="mission-room__log" data-cmc-watch-log="1">
          <div className="mission-room__section-head">
            <div><span>Watch log</span><small>Believed · Doubted · Bought · Did · Proved · Learned</small></div>
          </div>
          <div className="mission-room__plane-grid">
            <div><span>Believed</span><b>${believed.join(" · ") || "—"}</b></div>
            <div><span>Doubted</span><b>${doubted.join(" · ") || "—"}</b></div>
            <div><span>Bought</span><b>${bought.join(" · ") || "—"}</b></div>
            <div><span>Did</span><b>${watchLog.did || "—"}</b></div>
            <div><span>Proved</span><b>${watchLog.proved || "—"}</b></div>
            <div><span>Learned</span><b>${learned.join(" · ") || "—"}</b></div>
            <div><span>Handover</span><b>${(watchLog.handover && watchLog.handover.from) || "watch"} → ${(watchLog.handover && watchLog.handover.to) || "next_watch"}</b></div>
          </div>
        </section>` : null}
        <details className="mission-room__practice" data-cmc-adversarial="1" open=${advBadges.length ? true : undefined}>
          <summary>Practice · labelled tricks · never a certificate incident</summary>
          ${advBadges.map((b, i) => html`<p key=${i} className="note">${b}</p>`)}
          <div className="row gap">
            <${Field} label="Trick to try">
              <${Input} value=${advTrick} onInput=${(e) => setAdvTrick(e.target.value)}
                placeholder="hood / storm door / unplug lens"/>
            </${Field}>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${busyOrFrozen}
            onClick=${() => control("tick", { trigger: "adversarial", trick: advTrick })}>Log labelled trick</button>
        </details>
        ${implantSteps.length ? html`
        <section className="mission-room__implant" data-cmc-team-implant="1">
          <div className="mission-room__section-head">
            <div><span>Jobs on this watch</span><small>Roles on this case — not a named-agent cast</small></div>
            <em>${implant.roles && implant.roles.length ? implant.roles.length + " seats" : ""}</em>
          </div>
          <div className="mission-room__plane-grid">
            ${implantSteps.map((step) => html`
              <div key=${step.role}>
                <span>${operatorSay(step.role, "Seat")}</span>
                <b>${operatorSay(step.did, "On this watch")}</b>
                <small>${operatorSay(step.reason || (step.live_executed ? "live" : "no live walk"), "On this watch")}</small>
              </div>`)}
          </div>
        </section>` : null}
        ${addCaps.length || cancelCaps.length ? html`
        <section className="mission-room__plan-delta" data-cmc-plan-delta="1">
          <div className="mission-room__section-head">
            <div><span>Plan changes</span><small>Keep what is running. Add or drop only what is still pending.</small></div>
          </div>
          <p className="note"><b>Keep</b> · ${capsSay(keepCaps)}</p>
          <p className="note"><b>Add</b> · ${capsSay(addCaps)}</p>
          <p className="note"><b>Cancel pending</b> · ${capsSay(cancelCaps)}</p>
        </section>` : null}
        <${TechnicalDetails} title="Proof & detail">
        <p className="note" data-cmc-watch-genome=${genome.watch_genome_id || ""} data-epoch=${genome.epoch_id || ""}>
          ${genomeReason === "no_change"
            ? "Same facts · the Watch did not change its mind"
            : (genome.epoch_id ? "This judgement is bound to this Watch." : "This Watch has not opened a judgement yet.")}
        </p>
        <section className="mission-room__control-strip" data-cmc-autonomy-planes="1"
            data-health=${planes.runtime_health || ""} data-ledger=${planes.ledger || ""}
            data-fault=${planes.fault ? "1" : "0"} data-configured=${planes.configured ? "1" : "0"}>
          <div><span>How it should run</span><b>${planes.configured ? operatorSay(planes.configured_mode || "—", "—") : "Off"}</b></div>
          <div><span>How it runs</span><b>${operatorSay(planes.effective_mode || "—", "—")}</b></div>
          <div data-tone=${planes.fault ? "warn" : "go"}><span>Health</span><b>${operatorSay(planes.runtime_health || "—", "—")}</b></div>
          <div><span>Record</span><b>${operatorSay(planes.ledger || ledger, "—")}</b></div>
        </section>
        <section className="mission-room__control-strip" data-cmc-four-visibles="1"
            data-cmc-audience="tenant-plus-global-rails"
            data-origin=${four.origin || planes.origin_source || ""}
            aria-label="Desired vs Effective · revision · ACK · why blocked">
          <div data-cmc-desired-effective="Desired vs Effective"><span>How it should run</span><b>${operatorSay(four.desired, four.desired || "—")}</b></div>
          <div><span>Rule version</span><b>${four.rev}</b></div>
          <div data-cmc-ack-lag="ACK / lag"><span>Boards answered</span><b>${four.ack}</b></div>
          <div><span>Why blocked</span><b>${whySay(four.why)}</b></div>
        </section>
        <//>

        ${needs.length ? html`
          <section className="mission-room__needs" data-cmc-needs-you="eligible-only">
            <span className="eyebrow">Needs you</span>
            <b>${decision}</b>
            <small>${needWhy}. The case stays open until this is resolved.</small>
          </section>` : null}

        ${!isSoft && needs.length
          ? html`<p className="note" data-cmc-needs-you="incident-only">
              ${needs.length} still need you on this case · queue stays on Response</p>`
          : null}

        <section className="mission-room__five" data-cmc-mission-default="five-questions">
          <article><span>What happened?</span><b>${operatorSay(mission.NOW || "Team starting", "Team starting")}</b></article>
          <article><span>How severe?</span><b>${severity}</b></article>
          <article><span>Who is responsible?</span><b>${controlHolder}</b></article>
          <article><span>What happens next?</span><b>${operatorSay(mission.NEXT || "Waiting for the first look", "Waiting for the first look")}</b></article>
          <article data-tone=${needs.length ? "warn" : "quiet"}>
            <span>What needs my decision?</span><b>${decision}</b>
          </article>
        </section>

        <section className="mission-room__ai-brief" data-cmc-ai-output-types="typed">
          <div className="mission-room__section-head">
            <div><span>Team brief</span><small>Suggestion and action stay separate.</small></div>
            <em>${aiOutputs.length ? aiOutputs.length + " notes" : ""}</em>
          </div>
          <div className="mission-room__ai-type-key" aria-label="AI output types">
            ${AI_OUTPUT_TYPES.map((kind) => html`<span key=${kind}>${kind}</span>`)}
          </div>
          ${aiOutputs.length ? html`<div className="mission-room__ai-output-list">
            ${aiOutputs.slice(0, 6).map((item, i) => html`
              <article key=${item.id || i} data-tone=${outputTone(item.output_type)}>
                <span>${operatorSay(item.output_type || "Assessment", "Assessment")}</span>
                <b>${item.text || "No narrative supplied"}</b>
                <small>${operatorSay(item.source, "AI Operations Team")}</small>
              </article>`)}
          </div>` : html`<p className="note">No AI output yet. Empty is not a recommendation.</p>`}
        </section>

        ${commandPlanes.length ? html`
          <section className="mission-room__command-plane" data-cmc-command-planes="orthogonal">
            <div className="mission-room__section-head">
              <div><span>Latest command</span><small>Authority, delivery, and proof are independent.</small></div>
            </div>
            <div className="mission-room__plane-grid">
              <div><span>Who decided</span><b>${operatorSay(commandPlanes[0].intent_authority?.state || "draft", "Draft")}</b></div>
              <div><span>Delivery</span><b>${operatorSay(commandPlanes[0].delivery?.state || "queued", "Queued")}</b></div>
              <div><span>Proof</span><b>${operatorSay(commandPlanes[0].outcome_proof?.state || "unobserved", "Not seen yet")}</b></div>
            </div>
          </section>` : null}

        <details className="mission-room__controls-more">
          <summary>More control</summary>
          <div className="btn-row mission-room__controls">
            <button type="button" className="btn btn--ghost btn--sm" disabled=${controlBusy || !held || stopped}
              onClick=${() => control("continue")}>Continue</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${busyOrFrozen}
              data-cmc-rehearse="1" onClick=${() => control("rehearse")}>Rehearse</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${busyOrFrozen}
              data-cmc-rehearse-ghost="1" onClick=${() => control("tick", { trigger: "rehearsal_ghost" })}>Rehearse if due</button>
            <button type="button" className="btn btn--danger btn--sm" disabled=${controlBusy || human}
              onClick=${() => control("takeover")}>Takeover</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${controlBusy || !human}
              onClick=${() => control("release")}>Release to Team</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${controlBusy || !stopped}
              data-cmc-estop-release="1" onClick=${() => control("estop/release")}>Release E-Stop</button>
            <button type="button" className="btn btn--ghost btn--sm"
              onClick=${() => go("/settings/ai")}>AI policy</button>
          </div>
          <div className="row gap" style=${{ marginTop: 8 }}>
            <${Field} label="Tell the team to keep this">
              <${Input} value=${interveneNote} onInput=${(e) => setInterveneNote(e.target.value)}
                placeholder="Fact or constraint the Team must keep"/>
            </${Field}>
          </div>
        </details>
        <section className="mission-room__vision" data-cmc-s13-vision="1" style=${{ marginTop: 10 }}>
          <p className="eyebrow">Ask about this frame. Same watch — not a second console.</p>
          <div className="row gap">
            <${Field} label="Ask the frame">
              <${Input} value=${visionQ} onInput=${(e) => setVisionQ(e.target.value)}
                placeholder="What is in this frame?"/>
            </${Field}>
          </div>
          <div className="btn-row" style=${{ marginTop: 6 }}>
            <button type="button" className="btn btn--sm" disabled=${controlBusy || visionBusy || isSoft}
              data-cmc-s13-vision-ask="1" onClick=${() => vision("ask")}>Ask this frame</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${controlBusy || visionBusy || isSoft}
              data-cmc-s13-vision-refresh="1" onClick=${() => vision("refresh")}>Refresh this frame</button>
          </div>
          ${visionOut ? html`<p className="note" data-cmc-s13-vision-out="1">
            ${operatorSay(visionOut.answer || visionOut.status, "Vision answered")}
          </p>` : null}
        </section>

        <details className="mission-room__depth">
          <summary>Open mission evidence & diagnostics</summary>
        <${Alert} tone="info" title="Ownership fence"
          say="There is one dispatch record. This watch does not invent a second one."/>
        ${live
          ? html`<${Alert} tone="info" title="Hold is saved"
              say="This Hold is seen by every worker. An old Hold is refused."/>`
          : html`<${Alert} tone="warn" title="Hold is on this worker"
              say="This Hold lives in one worker until the live store is proven. The four workers do not share it yet."/>`}
        ${mission.needs_you_n ? html`
          <${Alert} tone="warn" title=${"Needs You · " + mission.needs_you_n}
            say=${(mission.needs_you || []).map((n) => operatorSay(n.title || n.kind, "")).filter(Boolean).join(" · ") || "Exception queue"}/>`
          : html`<p className="note">Nothing needs you. The team is running.</p>`}
        ${mission.hardware && mission.hardware.pending_n
          ? html`<${Alert} tone="info" title=${"Board proposals · " + mission.hardware.pending_n}
              say="These are proposed board steps only. They do not go out until live. Hold cancels what is not delivered."/>`
          : null}
        <div className="mission-room__lanes">
          ${lane("NOW", operatorSay(mission.NOW, "—"), "go")}
          ${lane("NEXT", operatorSay(mission.NEXT, "—"), "ai")}
          ${lane("WATCH", operatorSay(mission.WATCH, "—"), mission.contested_n ? "warn" : "ai")}
          ${lane("REALITY", operatorSay(mission.REALITY, "—"), "ai")}
          ${lane("PROOF", operatorSay(mission.PROOF, "—"), proofGreen ? "go" : "warn")}
        </div>
        <div className="dist" style=${{ marginTop: 10 }}>
          <${KV} k="Who is in charge" v=${operatorSay(auth, "—")} tone=${authorityTone(auth)}/>
          <${KV} k="Change count" v=${String(mission.control_generation || run.control_generation || "—")}/>
          <${KV} k="Record" v=${operatorSay(ledger, "—")} tone=${live ? "go" : "warn"}/>
          <${KV} k="Live" v=${live ? "Live store" : "Not Live yet"} tone=${live ? "go" : "warn"}/>
          <${KV} k="Open claims" v=${String(mission.claims_n != null ? mission.claims_n : "—")}/>
          <${KV} k="Contested" v=${String(mission.contested_n != null ? mission.contested_n : "—")}
            tone=${mission.contested_n ? "warn" : "go"}/>
          <${KV} k="Model on file" v=${operatorSay(mission.effective_model_id || run.effective_model_id, "On file")}/>
          <${KV} k="How it runs" v=${operatorSay(mission.via || run.via, "On this host")}
            tone=${mission.model_degraded || run.model_degraded ? "warn" : "go"}/>
          ${mission.playbook_id ? html`<${KV} k="Playbook" v=${operatorSay(mission.playbook_id, "Playbook on file")}/>` : null}
        </div>

        <div className="mission-room__aiw3" data-cmc-aiw="aiw-3" style=${{ marginTop: 14 }}>
          <${Alert} tone="info" title="Assessment on this watch"
            say="Mission Room owns reason / score / safety / evidence. AI owns the Live plan. Team keeps running; you Hold / Takeover on exceptions only."/>
          <div className="dist" style=${{ marginBottom: 8 }}>
            <${KV} k="Score" v=${assessment.score != null ? String(assessment.score) : "—"}
              tone=${assessment.score_owner === "ai" && independent ? "go" : "warn"}/>
            <${KV} k="Who scored this" v=${scoreOwnerLabel(assessment.score_owner)}
              tone=${assessment.score_owner === "ai" && independent ? "go" : "warn"}/>
            <${KV} k="Echo lamp" v=${assessment.echo_lamp === "on" ? "On · rules shadow" : "Off"}
              tone=${assessment.echo_lamp === "on" ? "warn" : ""}/>
            <${KV} k="Independence" v=${independent ? "Up · proven" : "Down · unproven"}
              tone=${independent ? "go" : "warn"}/>
            <${KV} k="Brain honesty" v=${brainHonestyLabel(
              mission.brain_honesty || lastCoord.brain_honesty,
              auth,
            )} tone=${brainHonestyTone(mission.brain_honesty || lastCoord.brain_honesty, auth)}/>
            <${KV} k="What the team planned" v=${
              Array.isArray(lastCoord.model_caps) && lastCoord.model_caps.length
                ? capsSay(lastCoord.model_caps)
                : operatorSay(lastCoord.intents_source, "None yet")
            } tone=${
              auth === "running_ai"
                && String(mission.brain_honesty || lastCoord.brain_honesty || "") === "coordinator"
                && Array.isArray(lastCoord.model_caps)
                && lastCoord.model_caps.length
                ? "go"
                : "warn"
            }/>
            <${KV} k="Where live steps came from" v=${liveCapsOriginLabel(liveCapsOrigin)}
              tone=${
                auth === "running_ai"
                && Array.isArray(liveCapsOrigin.coordinator)
                && liveCapsOrigin.coordinator.length
                ? "go"
                : "warn"
              }/>
            <${KV} k="Nearby cameras" v=${h3NearbyLabel(h3Nearby)}
              tone=${Number(h3Nearby.added || 0) > 0 ? "go" : "warn"}/>
            <${KV} k="Person on site" v=${h5DispatchLabel(h5Dispatch)}
              tone=${h5Dispatch.arrival === true ? "go" : "warn"}/>
            <${KV} k="Safety" v=${operatorSay(assessment.safety, "—")}
              tone=${assessment.safety === "clear" ? "go" : "warn"}/>
            <${KV} k="Evidence gaps" v=${String(assessment.evidence_gap_n != null ? assessment.evidence_gap_n : "—")}
              tone=${assessment.evidence_gap_n ? "warn" : "go"}/>
          </div>
          ${reasons.length ? html`
            <p className="eyebrow" style=${{ marginBottom: 4 }}>Reasons</p>
            <div className="rows">
              ${reasons.map((r, i) => html`
                <div key=${"r" + i} className="row row--say">
                  <span className="main"><b>Reason</b><span>${operatorSay(r, "On this watch")}</span></span>
                </div>`)}
            </div>` : html`<p className="note">No reasons yet.</p>`}
          ${evidenceGaps.length ? html`
            <p className="eyebrow" style=${{ margin: "10px 0 4px" }}>Evidence gaps</p>
            <div className="rows">
              ${evidenceGaps.map((g, i) => html`
                <div key=${g.claim_id || ("g" + i)} className="row row--say" data-tone="warn">
                  <span className="main"><b>${operatorSay(g.role, "Gap")}</b><span>${operatorSay(g.predicate, "Needs a look")}</span></span>
                </div>`)}
            </div>` : null}
        </div>

        <div className="mission-room__cf" data-cmc-aiw="aiw-cf" style=${{ marginTop: 14 }}>
          <${Alert} tone="warn" title=${"Counterfactual · what would change"}
            say="Preview execute / wait / alternative without blocking Team — not approve-every-step Intent Preview. Mid/high impact stays visible; Hold / Takeover remains the control surface."/>
          <div className="dist">
            <${KV} k="Execute" v=${operatorSay(counterfactual.execute, "—")}/>
            <${KV} k="Wait" v=${operatorSay(counterfactual.wait, "—")}/>
            <${KV} k="Alternative" v=${operatorSay(counterfactual.alternative, "—")}/>
            <${KV} k="Blocks Team"
              v=${counterfactual.blocks_team ? "Yes · this would stop the Team" : "No · Team keeps running"}
              tone=${counterfactual.blocks_team ? "bad" : "go"}/>
          </div>
        </div>

        <div className="mission-room__aiw7" data-cmc-aiw="aiw-7" style=${{ marginTop: 14 }}>
          <${Alert} tone="info" title="Shared Belief Board"
            say="Typed Claims with support/refute — Soft invent chat answers deleted. Contested stays visible so you see why Team changed its mind."/>
          <div className="dist" style=${{ marginBottom: 8 }}>
            <${KV} k="Active" v=${String(belief.active_n != null ? belief.active_n : "—")}/>
            <${KV} k="Contested" v=${String(belief.contested_n != null ? belief.contested_n : "—")}
              tone=${belief.contested_n ? "warn" : "go"}/>
            <${KV} k="Board" v=${belief.live ? "Live" : "—"} tone=${belief.soft_invent ? "bad" : "go"}/>
          </div>
          ${contested.length ? html`
            <p className="eyebrow" style=${{ marginBottom: 4 }}>Contested</p>
            <div className="rows">
              ${contested.map((c, i) => html`
                <div key=${c.claim_id || i} className="row row--say" data-tone="warn">
                  <span className="main"><b>Contested</b><span>${claimLine(c)}</span></span>
                </div>`)}
            </div>` : html`<p className="note">No contested claims.</p>`}
          ${activeHead.length ? html`
            <p className="eyebrow" style=${{ margin: "10px 0 4px" }}>Active Claims</p>
            <div className="rows">
              ${activeHead.slice(0, 6).map((c, i) => html`
                <div key=${c.claim_id || ("a" + i)} className="row row--say">
                  <span className="main"><b>${operatorSay(c.role, "Claim")}</b><span>${claimLine(c)}</span></span>
                </div>`)}
            </div>` : null}

          <p className="eyebrow" style=${{ margin: "14px 0 4px" }}>Objective DAG</p>
          ${dagNodes.length ? html`
            <div className="rows" data-cmc-aiw7="objective-dag">
              ${dagNodes.map((n, i) => html`
                <div key=${n.id || i} className="row row--say">
                  <span className="main">
                    <b>${n.kind === "objective" ? "Objective" : "Step"} · ${operatorSay(n.title, "On this watch")}</b>
                    <span>${operatorSay(n.status, "—")}${n.desk ? " · " + operatorSay(n.desk, n.desk) : ""}</span>
                  </span>
                </div>`)}
            </div>` : html`<p className="note">No executable objectives yet.</p>`}

          <p className="eyebrow" style=${{ margin: "14px 0 4px" }} data-cmc-aiw7-proof="Proof-of-Outcome">Proof of what happened</p>
          <div className="dist" data-cmc-aiw7="proof" data-cmc-proof-grade=${proofGrade} data-receipt-is-not-verified="1">
            <${KV} k="Stage" v=${operatorSay(proofStage, "Not verified yet")}
              tone=${proofGreen ? "go" : "warn"}/>
            <${KV} k="Label" v=${operatorSay(proof.label || mission.PROOF, "—")}/>
            <${KV} k="Green complete"
              v=${proofGreen ? "Complete" : "Not proven yet"}
              tone=${proofGreen ? "go" : "warn"}/>
            <${KV} k="Receipt vs proof"
              v=${operatorSay(proofGrade, "Receipt is not proof")}
              tone=${proofGrade === "verified" ? "go" : "warn"}/>
          </div>
          <p className="note">A send is not a result. A receipt is never verified.</p>

          <p className="eyebrow" style=${{ margin: "14px 0 4px" }} data-cmc-incident-sor="Incident SoR close">Close this case</p>
          <div className="dist" data-cmc-s-close-sor="1" data-receipt-is-not-verified="1">
            <${KV} k="This case"
              v=${operatorSay(closeSor.incident_status, "Open")}
              tone=${closeSor.incident_status === "closed" ? "go" : "warn"}/>
            <${KV} k="Team closed"
              v=${closeSor.team_closed === true ? "Yes" : "No"}
              tone=${closeSor.team_closed === true && closeSor.incident_status === "closed" ? "go" : "warn"}/>
            <${KV} k="Close ready"
              v=${closeSor.ready === true ? "Yes" : whySay(closeReasons[0] || "not ready")}
              tone=${closeSor.ready === true ? "go" : "warn"}/>
          </div>
          <p className="note">The case must close on its own record. A receipt is never a verified close. There is no second close button.</p>

          <p className="eyebrow" style=${{ margin: "14px 0 4px" }} data-cmc-intent-proof="Intent → Proof audit">From plan to proof</p>
          <div className="dist" data-cmc-audit-chain="1" data-soft-invent="0">
            <${KV} k="Steps" v=${String(auditChain.steps_n || auditSteps.length || 0)}/>
            <${KV} k="Intact"
              v=${auditChain.intact === true ? "Yes" : (auditChain.ok === false ? "Unavailable" : "Empty or broken")}
              tone=${auditChain.intact === true ? "go" : "warn"}/>
            <${KV} k="Event→Proof"
              v=${auditChain.event_to_proof === true ? "Complete" : "Open"}
              tone=${auditChain.event_to_proof === true ? "go" : "warn"}/>
            ${!isSoft && liveLoopN ? html`<${KV} k="Live loops"
              v=${`${liveLoopClosed} of ${liveLoopN} closed · ${liveLoopPlaying} playing`}
              tone=${liveLoopOpen ? "warn" : "go"}/>` : null}
          </div>
          ${!isSoft && liveLoopN > 1 ? html`<p className="note" data-cmc-live-loops="1">Several live cameras may play at once. Close waits until each camera has play or its own snapshot · Soft invent one-live mutex deleted.</p>` : null}
          ${auditSteps.length ? html`
            <div className="rows" data-cmc-audit-chain-steps="1" style=${{ marginTop: 6 }}>
              ${auditSteps.slice(-12).map((st, i) => html`
                <div key=${st.step_hash || i} className="row row--say">
                  <span className="main">
                    <b>${operatorSay(st.phase, "Step")} · ${operatorSay(st.decision || st.reason_code, "Noted")}</b>
                    <span>${operatorSay(st.capability, "A planned step")}</span>
                  </span>
                </div>`)}
            </div>` : html`<p className="note">No steps yet. The team has not started.</p>`}
        </div>

        ${Array.isArray(mission.playbook_steps) && mission.playbook_steps.length ? html`
          <div className="rows" style=${{ marginTop: 10 }}>
            ${mission.playbook_steps.map((s, i) => html`
              <div key=${s.step_id || i} className="row row--say">
                <span className="main">
                  <b>${operatorSay(s.title || s.step_id || "step", "Step")}</b>
                  <span>${operatorSay(s.status || "pending", "Pending")} · ${operatorSay(s.desk, "—")}</span>
                </span>
              </div>`)}
          </div>` : null}
        <div className="btn-row" style=${{ marginTop: 12 }}>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${controlBusy || held}
            onClick=${() => control("tick")}>Look again</button>
        </div>
        <p className="note" style=${{ marginTop: 8 }}>
          Empty lanes stay blank. Practice never writes a live record. At most two human touches on a normal case.
        </p>
        </details>
      <//>` : null}
    <//>
  <//>`;
}

export default MissionRoomPanel;
